import {
  BriefcaseIcon,
  DocumentTextIcon,
  OfficeBuildingIcon,
} from "@heroicons/react/outline";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  requestListBusinessServiceCategories,
  useBusinessServiceDispatch,
  useBusinessServiceState,
} from "../../Context";
import { Alert } from "../../Components/Alert";
import { Button } from "../../Components/Button";
import { useNavigate } from "react-router-dom";
import { EmptyList } from "../../Components/EmptyList";
import BusinessServiceCard from "../../Components/BusinessServiceCard";

const ManageBusinessServiceList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const dispatch = useBusinessServiceDispatch();
  const { businessServiceCategories, loading, error } =
    useBusinessServiceState();

  useEffect(() => {
    requestListBusinessServiceCategories(dispatch, {
      page: 1,
      limit: 10,
    });
  }, [dispatch]);

  const services = useMemo(() => {
    return businessServiceCategories
      .map((category) => {
        switch (category.type) {
          case "BUSINESS_ESTABLISHMENT":
            return {
              title: t("businessLicenses.establishmentTitle"),
              href: `/manage-business-service/${category.id}`,
              icon: OfficeBuildingIcon,
              iconForeground: "text-blue-600",
              iconBackground: "bg-blue-50",
              description: t("businessLicenses.establishmentDesc"),
            };
          case "DEED_AMENDMENT":
            return {
              title: t("businessLicenses.deedAmendmentTitle"),
              href: `/manage-business-service/${category.id}`,
              icon: DocumentTextIcon,
              iconForeground: "text-teal-600",
              iconBackground: "bg-teal-50",
              description: t("businessLicenses.deedAmendmentDesc"),
            };
          default:
            return {
              title: category.name,
              href: `/manage-business-service/${category.id}`,
              icon: BriefcaseIcon,
              iconForeground: "text-gray-600",
              iconBackground: "bg-gray-50",
              description: category.description,
            };
        }
      })
      .filter(Boolean);
  }, [businessServiceCategories, t]);

  const ServiceSkeleton = ({ length }) => {
    return (
      <div className="grid grid-cols-6 gap-4">
        {Array.from({ length }).map((_, serviceIdx) => (
          <div key={serviceIdx} className="col-span-6 sm:col-span-3 lg:col-span-2">
            <div className="bg-white border border-slate-200/60 rounded-xl p-6 h-full flex flex-col animate-pulse">
              <div className="flex items-start space-x-4 flex-1">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                </div>
                <div className="flex-1 min-w-0 flex flex-col h-full">
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-1"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-1"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 mb-4 flex-1"></div>
                  <div className="mt-auto">
                    <div className="w-20 h-10 bg-gray-200 rounded-lg"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return <ServiceSkeleton length={2} />;
  }

  if (error) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={error.message}
          errors={error.errors}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-wrap sm:w-full justify-between gap-4">
      <div className="flex flex-col gap-4 justify-around sm:flex-row">
        <div className="flex gap-4 w-full">
          <Button
            outline
            onClick={() => navigate("/business-service/order")}
            title="viewOrderHistory"
          />
          <Button
            outline
            onClick={() => navigate("/manage-business-service/addons")}
            title="manageAddOns"
          />
          <Button
            onClick={() => navigate("/manage-business-service/create")}
            title="createBusinessService"
          />
        </div>
      </div>
      {services.length > 0 ? (
        <div className="grid grid-cols-6 gap-4">
          {services.map((service, serviceIdx) => (
            <BusinessServiceCard key={serviceIdx} service={service} />
          ))}
        </div>
      ) : (
        <div className="bg-white col-span-12 rounded-lg p-4">
          <EmptyList title="noServicesAvailable" />
        </div>
      )}
    </div>
  );
};

export default ManageBusinessServiceList;
